import { brave } from "@/lib/wallet/solana/brave-quirk";

import type {
  KnownWalletId,
  QuirkEntry,
  WalletId,
} from "@/lib/wallet/solana/types";

/**
 * What each known Solana wallet needs from us, read from its own extension
 * build and docs on 2026-10-23 (`optionc-plan.md` §3; [X] marks what only a
 * real extension or device can confirm, in §11). Extension versions read:
 * Phantom 26.34.0, MetaMask 13.53.0, Coinbase 3.150.0, OKX 4.21.0, Trust
 * 26.42.4, Backpack 0.10.218, Solflare 2.40.1, Bitget 2.21.11, Exodus 26.9.21.
 *
 * `v1`, `send` and `reattach` are code-reviewed switches, like `VERIFIED`:
 * each changes what a wallet is trusted with, and flips only after that
 * wallet's own local and staging checks.
 */

const store = (id: string) => `https://chromewebstore.google.com/detail/${id}`;
const encoded = (href: string) => encodeURIComponent(href);
const origin = (href: string) => {
  try {
    return new URL(href).origin;
  } catch {
    return href;
  }
};
const withoutScheme = (href: string) => href.replace(/^https?:\/\//, "");

/** Phantom, holder-trusted and v1-tested (#560). */
const phantom: QuirkEntry = {
  id: "phantom",
  label: "Phantom",
  // Also registers "Phantom" Sui and Bitcoin wallets; the registry keeps the
  // Solana one by its features.
  names: ["Phantom"],
  v1: true,
  send: "wallet",
  reattach: "phantom-trusted",
  signIn: "standard",
  // Phantom refuses an input carrying `domain` before it shows anything.
  signInInput: ["nonce", "statement"],
  // One approval authorises both chains; it ignores `wallet_revokePermissions`.
  evm: {
    rdns: ["app.phantom"],
    flags: ["isPhantom"],
    sharesPermission: true,
    revokeIsSiteWide: false,
  },
  phone: {
    uaMarker: /\bPhantom\//i,
    deepLink: (href) =>
      `https://phantom.app/ul/browse/${encoded(href)}?ref=${encoded(origin(href))}`,
  },
  install: "https://phantom.com/download",
  privyClientType: "phantom",
};

/** MetaMask: one CAIP-25 permission for every chain; connect ignores `silent`. */
const metamask: QuirkEntry = {
  id: "metamask",
  label: "MetaMask",
  // Also registers a Bitcoin "MetaMask".
  names: ["MetaMask"],
  devnetNames: ["MetaMask Flask"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "standard",
  signInInput: ["nonce", "statement"],
  // EVM `wallet_revokePermissions` revokes the whole permission, Solana too.
  evm: {
    rdns: ["io.metamask", "io.metamask.flask"],
    flags: ["isMetaMask"],
    sharesPermission: true,
    revokeIsSiteWide: true,
  },
  phone: {
    uaMarker: /MetaMaskMobile/i,
    deepLink: (href) => `https://link.metamask.io/dapp/${withoutScheme(href)}`,
  },
  install: "https://metamask.io/download/",
};

/**
 * Coinbase: the desktop extension registers no Standard Solana wallet (only
 * legacy `window.coinbaseSolana`, behind a storage flag). Whether its app's
 * in-app browser registers one is [X] (#551's premise; D13).
 *
 * So in-app only (D13), and stg only until `VERIFIED` says otherwise: no
 * install page, since the extension it would send a desktop holder to cannot
 * connect Solana here. A phone is sent into the app; inside it, a registered
 * wallet connects like any other. The injected `window.coinbaseSolana`
 * source is D13's second branch, built only if the device sitting finds no
 * Standard wallet.
 */
const coinbase: QuirkEntry = {
  id: "coinbase",
  label: "Coinbase Wallet",
  names: ["Coinbase Wallet"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "standard",
  signInInput: ["nonce", "statement"],
  evm: {
    rdns: ["com.coinbase.wallet"],
    flags: ["isCoinbaseWallet"],
    sharesPermission: "unknown",
    revokeIsSiteWide: false,
  },
  phone: {
    uaMarker: /CoinbaseWalletRN/i,
    deepLink: (href) => `https://go.cb-w.com/dapp?cb_url=${encoded(href)}`,
  },
};

/** OKX: no `solana:signIn`; lists v1 (untested); sets `isMetaMask` and `isPhantom`. */
const okx: QuirkEntry = {
  id: "okx",
  label: "OKX Wallet",
  names: ["OKX Wallet"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "none",
  signInInput: ["nonce", "statement"],
  // Disconnect removes permissions per provider type.
  evm: {
    rdns: ["com.okex.wallet"],
    flags: ["isOkxWallet", "isOKExWallet"],
    sharesPermission: "unknown",
    revokeIsSiteWide: false,
  },
  // From Dynamic's wallet registry, not OKX's docs.
  phone: {
    deepLink: (href) => `okx://wallet/dapp/url?dappUrl=${encoded(href)}`,
  },
  install: store("mcohilncbfahbmgdjkbpemcciiolgcge"),
};

/** Trust: an EVM revoke disconnects the whole origin; lists only Solana mainnet. */
const trust: QuirkEntry = {
  id: "trust",
  label: "Trust Wallet",
  // Also registers a Bitcoin "Trust".
  names: ["Trust"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "standard",
  signInInput: ["nonce", "statement"],
  evm: {
    rdns: ["com.trustwallet.app"],
    flags: ["isTrust", "isTrustWallet"],
    sharesPermission: "unknown",
    revokeIsSiteWide: true,
  },
  phone: {
    deepLink: (href) =>
      `https://link.trustwallet.com/open_url?coin_id=501&url=${encoded(href)}`,
  },
  install: store("egjidjbpglichdcondbcbdnbeeppgdph"),
};

/** Backpack: per-chain connect; owns a non-configurable `window.ethereum` router. */
const backpack: QuirkEntry = {
  id: "backpack",
  label: "Backpack",
  // Also registers "Backpack" for Sui, Aptos, Bitcoin and Tron.
  names: ["Backpack"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "standard",
  signInInput: ["nonce", "statement"],
  evm: {
    rdns: ["app.backpack"],
    flags: ["isBackpack"],
    sharesPermission: "unknown",
    revokeIsSiteWide: false,
  },
  phone: {
    deepLink: (href) =>
      `https://backpack.app/ul/browse/?url=${encoded(href)}&ref=${encoded(origin(href))}`,
  },
  install: store("aflkmfhebedbjioipglgcbcmnbpgliof"),
  privyClientType: "backpack",
};

/** Solflare: Solana only, no EVM side at all. */
const solflare: QuirkEntry = {
  id: "solflare",
  label: "Solflare",
  names: ["Solflare"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "standard",
  signInInput: ["nonce", "statement"],
  phone: {
    deepLink: (href) =>
      `https://solflare.com/ul/v1/browse/${encoded(href)}?ref=${encoded(origin(href))}`,
  },
  install: store("bhhhlbepdkbapadjdnnojkbgioiodbic"),
  privyClientType: "solflare",
};

/** Bitget: sets `isMetaMask` and `isPhantom`; cross-chain permission [X]. */
const bitget: QuirkEntry = {
  id: "bitget",
  label: "Bitget Wallet",
  names: ["Bitget Wallet"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "standard",
  signInInput: ["nonce", "statement"],
  evm: {
    rdns: ["com.bitget.web3"],
    flags: ["isBitKeep"],
    sharesPermission: "unknown",
    revokeIsSiteWide: false,
  },
  phone: {
    deepLink: (href) => `https://bkcode.vip?action=dapp&url=${encoded(href)}`,
  },
  install: store("jiidiaalihmmhddjgbnbgdfflelocpak"),
};

/**
 * Exodus: origin-level trust, so either chain's disconnect ends both; can
 * take `window.phantom.*` and set `isPhantom` and `isMetaMask`. Lists only
 * Solana mainnet. No in-app deep link is known.
 */
const exodus: QuirkEntry = {
  id: "exodus",
  label: "Exodus",
  // Also registers "Exodus" for Ethereum, Algorand and Bitcoin.
  names: ["Exodus"],
  v1: false,
  send: "sign",
  reattach: "accounts-only",
  signIn: "standard",
  signInInput: ["nonce", "statement"],
  evm: {
    rdns: ["com.exodus.web3-wallet"],
    flags: ["isExodus"],
    sharesPermission: true,
    revokeIsSiteWide: true,
  },
  install: store("aholpfdialjgjfhomihkjbmgjidlcdno"),
};

export const QUIRKS: readonly QuirkEntry[] = [
  phantom,
  metamask,
  coinbase,
  okx,
  trust,
  backpack,
  solflare,
  bitget,
  exodus,
  brave,
];

/** Unsupported/embedded providers; Brave is an explicit external wallet. */
export const DENIED_NAMES: readonly string[] = ["Magic Eden", "Privy", "Glyph"];

export function quirk(id: KnownWalletId): QuirkEntry {
  const found = QUIRKS.find((entry) => entry.id === id);
  if (!found) throw new Error(`No quirk entry for ${id}.`);
  return found;
}

/** The known wallet a Standard name belongs to, on this build's network. */
export function quirkForName(
  name: string,
  devnet: boolean
): QuirkEntry | undefined {
  return QUIRKS.find(
    (entry) =>
      entry.names.includes(name) ||
      (devnet && (entry.devnetNames ?? []).includes(name))
  );
}

export function isKnownWalletId(value: string): value is KnownWalletId {
  return QUIRKS.some((entry) => entry.id === value);
}

export function quirkForId(id: WalletId): QuirkEntry | undefined {
  return isKnownWalletId(id) ? quirk(id) : undefined;
}
